import { Injectable, inject } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { AvailabilityVisitRequest } from "@app/models/dto/availability-visit-request";
import { AvailabilityVisitResponse } from "@app/models/dto/availability-visit-response";
import { BookingConfirmRequest } from "@app/models/dto/booking-confirm-request";
import { BookingConfirmResponse } from "@app/models/dto/booking-confirm-response";
import { ClientConfigService } from "@app/core/config/client-config.service";
import { firstValueFrom, Subject } from "rxjs";
import { AvailabilityBulkRequest } from "@app/services/http/availability-service/availability-bulk.request";
import { AvailabilityBulkResponse } from "@app/services/http/availability-service/availability-bulk.response";
import { BulkVisitTransformRequest } from "@app/models/dto/bulk-visit-transform-request";
import { BulkVisitTransformResponse } from "@app/models/dto/bulk-visit-transform-response";
import { takeUntil } from "rxjs/operators";
import { v4 as uuidv4 } from "uuid";
import { PlanningSegment } from "@app/models/rest/planning-segment";

@Injectable({
  providedIn: "root"
})
export class AvailabilityVisitService {
  private http = inject(HttpClient);
  private clientConfigService = inject(ClientConfigService);

  public cancelRequest$: Subject<void> = new Subject();
  public pendingUUID: string = null;

  public async getAvailabilityForVisit(
    availabilityRequest: AvailabilityVisitRequest
  ): Promise<AvailabilityVisitResponse> {
    const config = await this.clientConfigService.config;
    this.pendingUUID = uuidv4();
    availabilityRequest.requestUUID = this.pendingUUID;
    return firstValueFrom(
      this.http
        .post<AvailabilityVisitResponse>(
          config.serviceUrl + "/availability/visit",
          availabilityRequest,
          { withCredentials: true }
        )
        .pipe(takeUntil(this.cancelRequest$))
    );
  }

  public async clearPendingRequest(): Promise<any> {
    const config = await this.clientConfigService.config;
    this.cancelRequest$.next(); // Emit a value to signal cancellation
    this.cancelRequest$.complete(); // Optional: Complete the subject if no further cancellation is expected
    return firstValueFrom(
      this.http.post(
        config.serviceUrl + "/api/v1/availability/cancelRequest",
        this.pendingUUID,
        { withCredentials: true }
      )
    );
  }

  public async bookVisit(
    bookingConfirmRequest: BookingConfirmRequest
  ): Promise<BookingConfirmResponse> {
    const config = await this.clientConfigService.config;
    return (await firstValueFrom(
      this.http.post(
        config.serviceUrl + "/availability/visitConfirm",
        bookingConfirmRequest,
        { withCredentials: true }
      )
    )) as unknown as BookingConfirmResponse;
  }

  public async bulk(
    availabilityBulkRequest: AvailabilityBulkRequest
  ): Promise<AvailabilityBulkResponse> {
    const config = await this.clientConfigService.config;
    return (await firstValueFrom(
      this.http.post(
        config.serviceUrl + "/api/v1/availability/bulk",
        availabilityBulkRequest,
        { withCredentials: true }
      )
    )) as unknown as AvailabilityBulkResponse;
  }

  public async bulkVisitTransform(
    request: BulkVisitTransformRequest
  ): Promise<BulkVisitTransformResponse> {
    const config = await this.clientConfigService.config;
    return (await firstValueFrom(
      this.http.post(
        config.serviceUrl + "/api/v1/availability/visitTransform",
        request,
        { withCredentials: true }
      )
    )) as unknown as BulkVisitTransformResponse;
  }

  public async getSegments(priorityId: number): Promise<PlanningSegment[]> {
    const config = await this.clientConfigService.config;
    return (await firstValueFrom(
      this.http.get(config.serviceUrl + "/api/v1/availability/segments", {
        withCredentials: true,
        params: { priorityId: priorityId.toString() }
      })
    )) as unknown as any;
  }
}
